// react
import React, { useState, useEffect, useCallback, Suspense } from "react";

// react-i18next
import { useTranslation } from "react-i18next";

// @mui
import { Box, Typography, Grid, CircularProgress } from "@mui/material";

// components
import SectionCard from "./SectionCard";
import AuthorizedKeysStatusTable from "./AuthorizedKeysStatusTable";
import FlowStateTransitionsTable from "./FlowStateTransitionsTable";
import TableSkeleton from "./TableSkeleton";

// features/databox
import getFlowEvents from "features/databox/getFlowEvents";

// A ---------------------------------------------------------------------- M

const FlowMonitoringDashboard = ({ tag }) => {
  const { t } = useTranslation();

  const [sourcesData, setSourcesData] = useState([]);
  const [flowEventsData, setFlowEventsData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const { sources, events } = await getFlowEvents(tag);
      setSourcesData(sources || []);
      setFlowEventsData(events || []);
    } catch (error) {
      console.error("Error fetching sources events:", error);
      setError("Failed to load data. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, [tag]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography color="error" textAlign="center">
        {error}
      </Typography>
    );
  }

  return (
    <Grid item container spacing={2}>
      <Grid item xs={12} md={6}>
        <SectionCard title={t("sources_status")}>
          <Suspense fallback={<TableSkeleton rows={4} columns={4} />}>
            <AuthorizedKeysStatusTable sourcesData={sourcesData} />
          </Suspense>
        </SectionCard>
      </Grid>

      <Grid item xs={12} md={6}>
        <SectionCard title={t("flow_events")}>
          <Suspense fallback={<TableSkeleton rows={4} columns={3} />}>
            <FlowStateTransitionsTable flowEventsData={flowEventsData} />
          </Suspense>
        </SectionCard>
      </Grid>
    </Grid>
  );
};

export default FlowMonitoringDashboard;
